import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { client } from '../api/client';

type GenerationMode = 'curriculum' | 'wrong_answers' | 'photo' | 'school_plan' | null;

interface GenerationTask {
  id: number;
  source_type: string;
  status: string;
  generated_count: number;
  created_at: number;
  error_message?: string;
  progress_message?: string;
  progress_percent?: number;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

export default function QuestionGeneratorPage() {
  const { user } = useAuthStore();
  const [selectedMode, setSelectedMode] = useState<GenerationMode>(null);
  const [tasks, setTasks] = useState<GenerationTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Form states
  const [subjectId, setSubjectId] = useState('1');
  const [grade, setGrade] = useState('7');
  const [difficulty, setDifficulty] = useState('3');
  const [questionCount, setQuestionCount] = useState('10');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [selectedModel, setSelectedModel] = useState('google/gemini-2.0-flash-001');
  
  // Mode-specific states
  const [curriculumText, setCurriculumText] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [schoolPlanText, setSchoolPlanText] = useState('');

  useEffect(() => {
    // Verify real session before loading tasks
    verifySessionAndLoad();
  }, []);

  const verifySessionAndLoad = async () => {
    try {
      // Try to verify EdgeSpark session first
      const session = await client.auth.getSession();
      
      if (!session.data?.user) {
        // EdgeSpark session not found, check authStore (for phone login)
        const authUser = useAuthStore.getState().user;
        
        if (!authUser) {
          console.log('[Generator] No valid session or authStore user, redirecting to login');
          alert('登录已过期,请重新登录');
          useAuthStore.getState().reset();
          window.location.href = '/login';
          return;
        }
        
        console.log('[Generator] Using authStore user (phone login):', authUser.id);
      } else {
        console.log('[Generator] EdgeSpark session verified, user:', session.data.user.id);
      }
      
      await loadTasks();
    } catch (error) {
      console.error('[Generator] Session verification failed:', error);
      // Check authStore as fallback
      const authUser = useAuthStore.getState().user;
      if (authUser) {
        console.log('[Generator] EdgeSpark session failed, but authStore user exists:', authUser.id);
        await loadTasks();
      } else {
        alert('登录状态验证失败,请重新登录');
        useAuthStore.getState().reset();
        window.location.href = '/login';
      }
    }
  };

  const loadTasks = async () => {
    try {
      console.log('[Generator] Loading tasks');
      const response = await client.api.fetch('/api/public/generation-tasks');
      
      if (response.status === 401) {
        console.warn('[Generator] Unauthorized, checking authStore');
        const authUser = useAuthStore.getState().user;
        if (!authUser) {
          alert('登录已过期,请重新登录');
          useAuthStore.getState().reset();
          window.location.href = '/login';
        }
        // If authStore user exists, continue without redirecting
        return;
      }
      
      if (!response.ok) {
        console.warn('[Generator] Load tasks failed:', response.status);
        return;
      }
      
      const data = await response.json();
      setTasks(data.data || []);
    } catch (error) {
      console.error('[Generator] 加载任务失败:', error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Verify session before submitting (check both EdgeSpark and authStore)
      const session = await client.auth.getSession();
      const authUser = useAuthStore.getState().user;
      
      if (!session.data?.user && !authUser) {
        alert('登录已过期,请重新登录');
        useAuthStore.getState().reset();
        window.location.href = '/login';
        return;
      }

      console.log('[Generator] Submitting');
      
      let inputData: any = {
        subject_id: parseInt(subjectId),
        grade: parseInt(grade),
        difficulty: parseInt(difficulty),
        question_count: parseInt(questionCount),
        question_type: questionType,
      };

      if (selectedMode === 'photo') {
        if (!photoFile) {
          alert('请先选择或拍摄题目照片');
          setLoading(false);
          return;
        }
        
        const base64Image = await fileToBase64(photoFile);
        
        const photoPayload = {
          image: base64Image,
          mimeType: photoFile.type,
          grade: parseInt(grade),
          difficulty: parseInt(difficulty),
          questionCount: parseInt(questionCount),
          questionType: questionType,
          model: selectedModel,
        };
        
        console.log('[Generator] Photo-to-question request');
        
        const response = await client.api.fetch('/api/public/photo-to-question', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(photoPayload),
        });
        
        if (response.status === 401) {
          alert('登录已过期，请重新登录');
          window.location.href = '/login';
          return;
        }
        
        if (response.ok) {
          const result = await response.json();
          console.log('[Generator] Photo task created:', result);
          
          if (result.data?.extractedText) {
            setExtractedText(result.data.extractedText);
          }
          
          const taskId = result.data.taskId;
          setActiveTaskId(taskId);
          startPolling(taskId);
          
          setSelectedMode(null);
          resetForm();
          loadTasks();
        } else {
          const error = await response.text();
          console.error('[Generator] Photo task failed:', error);
          alert(`处理照片失败: ${response.status}`);
        }
        
        setLoading(false);
        return;
      }

      // Add mode-specific data
      if (selectedMode === 'curriculum') {
        inputData.curriculum_text = curriculumText;
      } else if (selectedMode === 'school_plan') {
        inputData.school_plan_text = schoolPlanText;
      }

      const payload = {
        source_type: selectedMode,
        input_data: inputData,
        model: selectedModel,
      };
      
      console.log('[Generator] Request payload:', payload);

      const response = await client.api.fetch('/api/public/generation-tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('[Generator] Response status:', response.status);

      if (response.status === 401) {
        alert('登录已过期，请重新登录');
        window.location.href = '/login';
        return;
      }

      if (response.ok) {
        const result = await response.json();
        console.log('[Generator] Task created:', result);
        
        // Start polling for progress
        const taskId = result.data.id;
        setActiveTaskId(taskId);
        startPolling(taskId);
        
        setSelectedMode(null);
        resetForm();
        loadTasks();
      } else {
        const error = await response.text();
        console.error('[Generator] Create task failed:', error);
        alert(`创建任务失败: ${response.status}`);
      }
    } catch (error) {
      console.error('[Generator] 创建任务失败:', error);
      alert('创建任务失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurriculumText('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setExtractedText(null);
    setSchoolPlanText('');
  };
  
  // Start polling for task progress
  const startPolling = (taskId: number) => {
    // Clear existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    // Poll every 2 seconds
    const interval = setInterval(async () => {
      try {
        const response = await client.api.fetch(`/api/public/generation-tasks/${taskId}`);
        if (response.ok) {
          const result = await response.json();
          const task = result.data;
          
          // Update task in list
          setTasks(prevTasks => 
            prevTasks.map(t => t.id === taskId ? task : t)
          );
          
          // Stop polling if task is completed or failed
          if (task.status === 'completed' || task.status === 'failed') {
            clearInterval(interval);
            setActiveTaskId(null);
            setPollingInterval(null);
            
            if (task.status === 'completed') {
              alert(`✅ 生成完成！成功生成 ${task.generated_count} 道题目`);
            } else {
              alert(`❌ 生成失败: ${task.error_message || '未知错误'}`);
            }
          }
        }
      } catch (error) {
        console.error('[Generator] Polling error:', error);
      }
    }, 2000);
    
    setPollingInterval(interval);
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const generationModes = [
    {
      id: 'curriculum' as const,
      title: '澳洲维州教学大纲',
      description: '基于Victorian Curriculum生成符合教学大纲的题目',
      icon: '📚',
      color: 'bg-blue-500',
    },
    {
      id: 'wrong_answers' as const,
      title: '错题集生成',
      description: '根据学生错题记录生成针对性练习题',
      icon: '❌',
      color: 'bg-red-500',
    },
    {
      id: 'photo' as const,
      title: '拍照导入题目',
      description: '通过拍照识别题目并批量导入题库',
      icon: '📸',
      color: 'bg-green-500',
    },
    {
      id: 'school_plan' as const,
      title: '学校教学计划',
      description: '根据学校教学计划生成配套练习题',
      icon: '🏫',
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            AI 题库生成器
          </h1>
          <p className="text-lg text-gray-600">
            选择生成方式，快速创建高质量题目
          </p>
        </div>

        {/* Generation Mode Selection */}
        {!selectedMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {generationModes.map((mode) => (
              <div
                key={mode.id}
                onClick={() => setSelectedMode(mode.id)}
                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl border-2 border-transparent hover:border-indigo-500"
              >
                <div className="flex items-start gap-4">
                  <div className={`${mode.color} w-16 h-16 rounded-lg flex items-center justify-center text-3xl shadow-md`}>
                    {mode.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {mode.title}
                    </h3>
                    <p className="text-gray-600">
                      {mode.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Generation Form */}
        {selectedMode && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {generationModes.find(m => m.id === selectedMode)?.title}
              </h2>
              <button
                onClick={() => {
                  setSelectedMode(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕ 取消
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI模型
                  </label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <optgroup label="Google Gemini (推荐)">
                      <option value="google/gemini-2.5-flash">Gemini 2.5 Flash</option>
                      <option value="google/gemini-2.0-flash-001">Gemini 2.0 Flash</option>
                      <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                    </optgroup>
                    <optgroup label="OpenAI">
                      <option value="openai/gpt-4o">GPT-4o</option>
                      <option value="openai/gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="openai/gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    </optgroup>
                    <optgroup label="Anthropic">
                      <option value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</option>
                      <option value="anthropic/claude-3-opus">Claude 3 Opus</option>
                    </optgroup>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    科目
                  </label>
                  <select
                    value={subjectId}
                    onChange={(e) => setSubjectId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="1">数学</option>
                    <option value="2">英语</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    年级
                  </label>
                  <select
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="7">Year 7</option>
                    <option value="8">Year 8</option>
                    <option value="9">Year 9</option>
                    <option value="10">Year 10</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    题型
                  </label>
                  <select
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="multiple_choice">选择题</option>
                    <option value="fill_blank">填空题</option>
                    <option value="short_answer">简答题</option>
                    <option value="essay">作文题</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    难度 (1-5)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    题目数量
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={questionCount}
                    onChange={(e) => setQuestionCount(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Mode-specific inputs */}
              {selectedMode === 'curriculum' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    教学大纲内容
                  </label>
                  <textarea
                    value={curriculumText}
                    onChange={(e) => setCurriculumText(e.target.value)}
                    placeholder="粘贴或输入Victorian Curriculum的教学内容..."
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              )}

              {selectedMode === 'wrong_answers' && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    系统将自动分析当前学生的错题记录，生成针对性练习题目。
                  </p>
                </div>
              )}

              {selectedMode === 'photo' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    上传题目照片
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setPhotoFile(file);
                          const reader = new FileReader();
                          reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className="cursor-pointer">
                      {photoPreview ? (
                        <div className="space-y-3">
                          <img 
                            src={photoPreview} 
                            alt="Preview" 
                            className="max-h-64 mx-auto rounded-lg shadow-md"
                          />
                          <p className="text-sm text-gray-600">{photoFile?.name}</p>
                          <p className="text-xs text-indigo-600">点击更换照片</p>
                        </div>
                      ) : (
                        <>
                          <div className="text-4xl mb-2">📸</div>
                          <p className="text-gray-600">点击拍照或上传题目照片</p>
                          <p className="text-xs text-gray-400 mt-2">支持 JPG, PNG 格式</p>
                        </>
                      )}
                    </label>
                  </div>
                  {photoFile && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-700">
                        ✓ 照片已选择，系统将自动识别题目文字并生成相似练习题
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedMode === 'school_plan' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    学校教学计划
                  </label>
                  <textarea
                    value={schoolPlanText}
                    onChange={(e) => setSchoolPlanText(e.target.value)}
                    placeholder="输入学校的教学计划内容..."
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '生成中...' : '开始生成'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Task History */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">生成任务记录</h2>
          
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📝</div>
              <p>暂无生成任务</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map((task) => {
                const isActive = task.id === activeTaskId;
                const showProgress = task.status === 'processing' || isActive;
                
                return (
                <div
                  key={task.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-900">
                          {generationModes.find(m => m.id === task.source_type)?.title}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          task.status === 'completed' ? 'bg-green-100 text-green-800' :
                          task.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          task.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {task.status === 'completed' ? '已完成' :
                           task.status === 'processing' ? '处理中' :
                           task.status === 'failed' ? '失败' : '待处理'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span>已生成: {task.generated_count} 题</span>
                        <span className="mx-2">•</span>
                        <span>{task.created_at ? new Date(task.created_at * 1000).toLocaleString('zh-CN') : '未知时间'}</span>
                      </div>
                      
                      {/* Progress indicator */}
                      {showProgress && task.progress_message && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex-1">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${task.progress_percent || 0}%` }}
                                />
                              </div>
                            </div>
                            <span className="text-xs text-gray-500 font-medium">
                              {task.progress_percent || 0}%
                            </span>
                          </div>
                          <p className="text-sm text-indigo-600 font-medium">
                            {task.progress_message}
                          </p>
                        </div>
                      )}
                      
                      {task.error_message && (
                        <div className="mt-2 text-sm text-red-600">
                          错误: {task.error_message}
                        </div>
                      )}
                    </div>
                    {task.status === 'completed' && (
                      <button className="text-indigo-600 hover:text-indigo-800 font-medium">
                        查看题目 →
                      </button>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
